import { monthNames } from "./consts";

export const ENGAGEMENT_DATA_START_YEAR = 2026;
export const ENGAGEMENT_DATA_START_MONTH = 7;

export interface EngagementPeriod {
  month: string;
  year: number;
}

function isEngagementDataAvailable(now: Date): boolean {
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  return (
    year > ENGAGEMENT_DATA_START_YEAR
    || (year === ENGAGEMENT_DATA_START_YEAR && month >= ENGAGEMENT_DATA_START_MONTH)
  );
}

export function monthNumberToName(month: number): string {
  return monthNames[month - 1] ?? monthNames[0];
}

export function monthNameToNumber(name: string): number {
  const index = monthNames.indexOf(name);
  return index >= 0 ? index + 1 : 1;
}

export function getEngagementSelectableYears(now = new Date()): number[] {
  if (!isEngagementDataAvailable(now)) {
    return [];
  }

  const currentYear = now.getFullYear();
  const count = currentYear - ENGAGEMENT_DATA_START_YEAR + 1;

  return Array.from(
    { length: count },
    (_, i) => ENGAGEMENT_DATA_START_YEAR + i
  );
}

export function getEngagementSelectableMonths(year: number, now = new Date()): string[] {
  if (!isEngagementDataAvailable(now)) {
    return [];
  }

  const currentYear = now.getFullYear();
  const currentMonthIndex = now.getMonth();
  const startMonthIndex = ENGAGEMENT_DATA_START_MONTH - 1;

  let fromIndex = 0;
  let toIndex = monthNames.length - 1;

  if (year === ENGAGEMENT_DATA_START_YEAR) {
    fromIndex = startMonthIndex;
  }

  if (year === currentYear) {
    toIndex = currentMonthIndex;
  }

  if (fromIndex > toIndex) {
    return [];
  }

  return monthNames.slice(fromIndex, toIndex + 1);
}

export function clampEngagementPeriod(
  month: string,
  year: number,
  now = new Date()
): EngagementPeriod {
  const selectableYears = getEngagementSelectableYears(now);

  if (selectableYears.length === 0) {
    return {
      month: monthNumberToName(ENGAGEMENT_DATA_START_MONTH),
      year: ENGAGEMENT_DATA_START_YEAR,
    };
  }

  let clampedYear = year;
  if (!selectableYears.includes(year)) {
    clampedYear = selectableYears[selectableYears.length - 1];
  }

  const selectableMonths = getEngagementSelectableMonths(clampedYear, now);

  if (selectableMonths.length === 0) {
    return {
      month: monthNumberToName(ENGAGEMENT_DATA_START_MONTH),
      year: ENGAGEMENT_DATA_START_YEAR,
    };
  }

  const clampedMonth = selectableMonths.includes(month)
    ? month
    : selectableMonths[selectableMonths.length - 1];

  return {
    month: clampedMonth,
    year: clampedYear,
  };
}

export function clampEngagementPeriodFromNumbers(
  month: number,
  year: number,
  now = new Date()
): EngagementPeriod {
  return clampEngagementPeriod(monthNumberToName(month), year, now);
}
